import { Component, signal, computed, effect, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MapPickerButtonComponent } from '../../shared/components/map-picker-dialog-component/mapPickerButtonComponent';
import { CheckoutFormState } from '../../core/models/interfaces-general';
import { MapPickerResult } from '../../services/mapPickerService-service';
import { CartService } from '../../services/cart.service';
import { PedidoService } from '../../core/services/pedido.service';
import { PagoService } from '../../core/services/pago.service';
import { WhatsappUtility } from '../../core/utility/whatsapp-utility';
import { ZonaService } from '../../core/services/zona.service';
import { EmpresaService } from '../../core/services/empresa.service';
import { MetodoPagoApi, TallerApi } from '../../core/models/api/empresa-api';
import { ConfirmarPedidoPayload } from '../../core/models/api/pedido-api';
import { CarritoBackService, CarritoCheckoutPayload, EstablecimientoDisponibleApi, ReservaApi } from '../../services/carrito-back.service';
import type { TiendaPickerResult } from './tienda-picker.component';
import { SafeImageComponent } from '../../shared/components/safe-image.component';
import { Constante } from '../../constante';

@Component({
    selector: 'app-checkout-stepper',
    standalone: true,
    imports: [CommonModule, FormsModule, MapPickerButtonComponent, SafeImageComponent],
    templateUrl: './checkout-stepper.component.html'
})
export class CheckoutStepperComponent implements OnInit, OnDestroy {
    cartService = inject(CartService);
    private zonaService = inject(ZonaService);
    private dialog = inject(MatDialog);
    private router = inject(Router);
    private pedidos = inject(PedidoService);
    private pagos = inject(PagoService);
    private empresa = inject(EmpresaService);
    private carritoBack = inject(CarritoBackService);

    // 1. SIGNAL PRINCIPAL DE ESTADO
    currentStep = signal<number>(1);

    form = signal<CheckoutFormState>({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        voucherType: 'boleta',
        docNumber: '',
        deliveryType: 'delivery',
        recipientType: 'other',
        recipientName: '',
        recipientLastName: '',
        recipientPhone: '',
        cardMessage: '',
        address: '',
        reference: '',
        location: undefined,
        paymentMethod: null,
        voucherFile: null,
        voucherFileName: null,
        termsAccepted: false
    });

    costoEnvio = signal<number>(0);
    zonaEnvio = signal<number | null>(null);
    zonaNombre = signal('');
    metodosPago = signal<MetodoPagoApi[]>([]);
    establecimientos = signal<EstablecimientoDisponibleApi[]>([]);
    taller = signal<TallerApi | null>(null);
    direccionesTienda = signal<Record<string, string>>({});
    private resumenReservaAplicado = false;
    submitting = signal<boolean>(false);
    submitError = signal<string | null>(null);
    pedidoUuid = signal<string | null>(null);
    pedidoNumero = signal('');
    pedidoEmail = signal('');
    pedidoTelefono = signal('');
    pedidoTotal = signal(0);
    copiado = signal(false);
    infoCopiada = signal(false);
    showErrors = signal(false);
    reserva = signal<ReservaApi | null>(null);
    segundosReserva = signal<number>(0);
    reservando = signal<boolean>(false);
    reservaError = signal<string | null>(null);
    private tickTimer: ReturnType<typeof setInterval> | null = null;
    private pedidoCerrado = false;

    // 2. COMPUTEDS PARA VALIDACIÓN PASO A PASO
    step1Valid = computed(() => {
        const f = this.form();
        const docLen = f.voucherType === 'boleta' ? 8 : 11;
        const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
        const isPhoneValid = this.digits(f.phone).length === 9;

        return (
            f.firstName.trim().length > 1 &&
            f.lastName.trim().length > 1 &&
            isPhoneValid &&
            isEmailValid &&
            f.docNumber.trim().length === docLen
        );
    });

    step2Valid = computed(() => {
        const f = this.form();
        const recibe = f.recipientType === 'self' ? true : this.recipientValid();
        if (f.deliveryType === 'pickup') {
            return recibe && !!f.establecimientoId;
        }

        return this.deliveryDisponible() && recibe && f.address.trim().length > 3 && f.location?.lat != null && f.location?.lng != null;
    });

    step3Valid = computed(() => {
        const f = this.form();
        return (
            f.paymentMethod !== null &&
            f.voucherFile !== null &&
            f.termsAccepted === true
        );
    });

    cartSubtotal = computed(() => this.cartService.subtotal());

    envioActual = computed(() => {
        if (this.form().deliveryType !== 'delivery') {
            return 0;
        }
        return this.zonaEnvio() ?? this.costoEnvio();
    });

    pickupDisponibles = computed(() => this.establecimientos().filter((e) => e.disponible > 0));

    tiendaSeleccionada = computed(() => {
        const id = this.form().establecimientoId;
        return id ? (this.establecimientos().find((e) => e.uuid === id) ?? null) : null;
    });

    deliveryDisponible = computed(() => this.establecimientos().some((e) => e.tieneDelivery && e.disponible > 0));

    deliveryPrecio = computed(() => {
        const precios = this.establecimientos()
            .filter((e) => e.tieneDelivery && e.disponible > 0 && e.precioDelivery != null)
            .map((e) => e.precioDelivery as number);
        return precios.length === 0 ? null : Math.min(...precios);
    });

    reservaActiva = computed(() => this.reserva() !== null && this.segundosReserva() > 0);

    metodoSeleccionado = computed(() => {
        const want = (this.form().paymentMethod ?? '').toLowerCase();
        const list = this.metodosPago();
        return list.find((m) => (m.tipoMetodoPago ?? '').toLowerCase().includes(want)) ?? list[0] ?? null;
    });

    mapaTallerLink(): string {
        const t = this.taller();
        if (!t) {
            return '';
        }
        if (t.urlMaps) {
            return t.urlMaps;
        }
        if (t.latitud != null && t.longitud != null) {
            return `https://www.google.com/maps/search/?api=1&query=${t.latitud},${t.longitud}`;
        }
        const q = [t.direccion, t.distrito].filter((v) => !!v).join(', ');
        return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : '';
    }

    orderTotal = computed(() => {
        return this.cartSubtotal() + this.envioActual();
    });

    // 3. EFFECT PARA AUDITORÍA / SINCRONIZACIÓN DE MAPA Y DIRECCIÓN
    constructor() {
        effect(() => {
            const loc = this.form().location;
            if (loc?.address && !this.form().address) {
                this.updateField('address', loc.address);
            }
        });

        effect(() => {
            if (this.resumenReservaAplicado || this.cartService.checkoutEstado() !== 'RESERVA') {
                return;
            }
            const expiraAt = this.cartService.expiraAt();
            if (!expiraAt) {
                return;
            }
            const restante = Math.max(0, Math.floor((Date.parse(expiraAt) - Date.now()) / 1000));
            if (restante <= 0) {
                this.cartService.limpiarReserva();
                return;
            }

            this.resumenReservaAplicado = true;
            const establecimientoId = this.cartService.establecimientoId();
            if (establecimientoId) {
                this.updateField('establecimientoId', establecimientoId);
            }
            this.reserva.set({
                establecimientoId: establecimientoId ?? '',
                establecimientoNombre: this.cartService.establecimientoNombre() ?? '',
                costoEnvio: this.cartService.costoEnvio(),
                expiraAt,
                segundosRestantes: restante
            });
            this.segundosReserva.set(restante);
            this.iniciarTimer();
            this.currentStep.set(3);
        });
    }

    ngOnInit(): void {
        this.empresa.getTaller().subscribe({
            next: (t) => this.taller.set(t),
            error: () => undefined
        });
        this.empresa.listMetodosPago().subscribe({
            next: (m) => this.metodosPago.set(m),
            error: () => this.metodosPago.set([])
        });
        this.cargarEstablecimientos();
    }

    // MÉTODOS DE MANEJO
    updateField<K extends keyof CheckoutFormState>(key: K, value: CheckoutFormState[K]) {
        this.form.update(prev => ({ ...prev, [key]: value }));
    }

    onLocationSelected(result: MapPickerResult) {
        this.form.update(prev => ({
            ...prev,
            location: result,
            address: result.address || prev.address
        }));
        if (result.lat != null && result.lng != null) {
            this.zonaService.cotizar(result.lat, result.lng).subscribe({
                next: (c) => {
                    this.zonaEnvio.set(c.precio);
                    this.zonaNombre.set(c.zonaNombre ?? '');
                },
                error: () => undefined
            });
        }
        if (this.form().deliveryType === 'delivery' && this.reserva() !== null) {
            this.reservar(false);
        }
    }

    abrirMapa(pick: { openPicker(): void }) {
        if (this.dialog.openDialogs.length > 0) {
            return;
        }
        if (this.form().address.trim()) {
            return;
        }
        pick.openPicker();
    }

    onFileSelected(event: Event) {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
            const file = target.files[0];
            this.form.update(prev => ({
                ...prev,
                voucherFile: file,
                voucherFileName: file.name
            }));
        }
    }

    nextStep() {
        if (this.currentStep() === 1 && this.step1Valid()) {
            this.showErrors.set(false);
            this.guardarCheckout('DATOS');
            this.cargarEstablecimientos();
            this.currentStep.set(2);
        } else if (this.currentStep() === 2 && this.step2Valid()) {
            this.showErrors.set(false);
            if (this.reservaActiva()) {
                this.currentStep.set(3);
            } else {
                this.reservar(true);
            }
        } else {
            this.showErrors.set(true);
            this.focusFirstInvalid();
        }
    }

    prevStep() {
        if (this.currentStep() <= 1) {
            return;
        }
        if (this.currentStep() === 2 && this.reserva() !== null) {
            this.liberarReserva();
        }
        this.currentStep.update(s => s - 1);
    }

    goToStep(step: number) {
        if (step === 1) {
            this.showErrors.set(false);
            this.currentStep.set(1);
        }
        if (step === 2 && this.step1Valid()) {
            this.showErrors.set(false);
            this.currentStep.set(2);
        }
        if (step === 3 && this.step1Valid() && this.step2Valid()) {
            this.showErrors.set(false);
            if (this.reservaActiva()) {
                this.currentStep.set(3);
            } else {
                this.reservar(true);
            }
        }
    }

    digits(value: string | null | undefined): string {
        return (value ?? '').replace(/\D/g, '');
    }

    recipientValid(): boolean {
        const f = this.form();
        return (
            f.recipientName.trim().length > 1 &&
            f.recipientLastName.trim().length > 1 &&
            this.digits(f.recipientPhone).length === 9
        );
    }

    errS1(field: 'firstName' | 'lastName' | 'phone' | 'email' | 'docNumber'): boolean {
        if (!this.showErrors()) {
            return false;
        }
        const f = this.form();
        switch (field) {
            case 'firstName': return f.firstName.trim().length <= 1;
            case 'lastName': return f.lastName.trim().length <= 1;
            case 'phone': return this.digits(f.phone).length !== 9;
            case 'email': return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
            case 'docNumber': return f.docNumber.trim().length !== (f.voucherType === 'boleta' ? 8 : 11);
        }
    }

    errS2(field: 'recipName' | 'recipLast' | 'recipPhone' | 'address'): boolean {
        if (!this.showErrors()) {
            return false;
        }
        const f = this.form();
        const needRecip = f.recipientType === 'other';
        switch (field) {
            case 'recipName': return needRecip && f.recipientName.trim().length <= 1;
            case 'recipLast': return needRecip && f.recipientLastName.trim().length <= 1;
            case 'recipPhone': return needRecip && this.digits(f.recipientPhone).length !== 9;
            case 'address': return f.deliveryType === 'delivery' && f.address.trim().length <= 3;
        }
    }

    private focusFirstInvalid() {
        const step = this.currentStep();
        const ids = step === 1
            ? ['s1-firstName', 's1-lastName', 's1-phone', 's1-email', 's1-docNumber']
            : ['s2-recip-name', 's2-recip-last', 's2-recip-phone', 's2-address'];
        const invalid = step === 1
            ? (['firstName', 'lastName', 'phone', 'email', 'docNumber'] as const).some((k) => this.errS1(k))
            : (['recipName', 'recipLast', 'recipPhone', 'address'] as const).some((k) => this.errS2(k));
        if (!invalid) {
            return;
        }
        for (const id of ids) {
            const el = document.getElementById(id) as HTMLElement | null;
            if (el && this.isFieldInvalid(step, id)) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.focus();
                return;
            }
        }
    }

    private isFieldInvalid(step: number, id: string): boolean {
        if (step === 1) {
            if (id === 's1-firstName') return this.errS1('firstName');
            if (id === 's1-lastName') return this.errS1('lastName');
            if (id === 's1-phone') return this.errS1('phone');
            if (id === 's1-email') return this.errS1('email');
            return this.errS1('docNumber');
        }
        if (id === 's2-recip-name') return this.errS2('recipName');
        if (id === 's2-recip-last') return this.errS2('recipLast');
        if (id === 's2-recip-phone') return this.errS2('recipPhone');
        return this.errS2('address');
    }

    cargarEstablecimientos() {
        const cartUuid = this.cartService.cartUuid();
        if (!cartUuid) {
            return;
        }

        this.carritoBack.listarEstablecimientos(cartUuid).subscribe({
            next: (res) => {
                if (res.success === Constante.REQUEST_STATUS_OK && Array.isArray(res.data)) {
                    this.establecimientos.set(res.data);
                }
            },
            error: () => undefined
        });
    }

    selectEstablecimiento(uuid: string) {
        this.updateField('establecimientoId', uuid);
        this.reservaError.set(null);
    }

    async abrirTiendaPicker() {
        const disponibles = this.pickupDisponibles();
        if (disponibles.length === 0) {
            return;
        }
        const { TiendaPickerComponent } = await import('./tienda-picker.component');
        const origen = this.form().location;
        this.dialog.open(TiendaPickerComponent, {
            data: {
                tiendas: disponibles,
                seleccionada: this.form().establecimientoId ?? null,
                origen: origen ? { lat: origen.lat, lng: origen.lng } : null
            },
            autoFocus: false,
            width: '920px',
            maxWidth: '95vw'
        }).afterClosed().subscribe((result: TiendaPickerResult | undefined) => {
            if (result?.uuid) {
                if (result.direccion) {
                    this.direccionesTienda.update((prev) => ({ ...prev, [result.uuid]: result.direccion as string }));
                }
                this.selectEstablecimiento(result.uuid);
            }
        });
    }

    direccionTienda(t: EstablecimientoDisponibleApi): string {
        return t.direccion || this.direccionesTienda()[t.uuid] || '';
    }

    linkMapaTienda(t: EstablecimientoDisponibleApi): string {
        return `https://www.google.com/maps/search/?api=1&query=${t.latitud},${t.longitud}`;
    }

    reservar(avanzar: boolean) {
        const cartUuid = this.cartService.cartUuid();
        if (!cartUuid || this.reservando()) {
            return;
        }
        const f = this.form();
        const isDelivery = f.deliveryType === 'delivery';
        this.reservando.set(true);
        this.reservaError.set(null);
        this.guardarCheckout('ENTREGA');
        this.carritoBack.reservar(cartUuid, {
            tipoEntregaId: isDelivery ? 1 : 2,
            establecimientoId: isDelivery ? null : f.establecimientoId,
            latitud: isDelivery ? (f.location?.lat ?? null) : null,
            longitud: isDelivery ? (f.location?.lng ?? null) : null
        }).subscribe({
            next: (res) => {
                this.reservando.set(false);
                if (res.success === Constante.REQUEST_STATUS_OK && res.data) {
                    this.resumenReservaAplicado = true;
                    this.reserva.set(res.data);
                    this.segundosReserva.set(Math.max(0, res.data.segundosRestantes));
                    this.zonaEnvio.set(null);
                    this.costoEnvio.set(res.data.costoEnvio ?? 0);
                    this.cartService.marcarReserva(res.data.expiraAt, res.data.establecimientoNombre, res.data.establecimientoId);
                    this.iniciarTimer();
                    if (avanzar) {
                        this.currentStep.set(3);
                    }
                } else {
                    this.reservaError.set(res.message || 'No se pudo reservar tu pedido.');
                }
            },
            error: (err) => {
                this.reservando.set(false);
                this.reservaError.set(err?.message || 'No se pudo reservar tu pedido.');
            }
        });
    }

    liberarReserva() {
        const cartUuid = this.cartService.cartUuid();
        this.detenerTimer();
        this.reserva.set(null);
        this.segundosReserva.set(0);
        this.reservaError.set(null);
        this.cartService.limpiarReserva();
        if (!cartUuid) {
            return;
        }
        this.carritoBack.liberarReserva(cartUuid).subscribe({ next: () => undefined, error: () => undefined });
    }

    guardarCheckout(estado: string) {
        const cartUuid = this.cartService.cartUuid();
        if (!cartUuid) {
            return;
        }
        this.carritoBack.guardarCheckout(cartUuid, this.buildCheckoutPayload(estado)).subscribe({ next: () => undefined, error: () => undefined });
    }

    tiempoReserva(): string {
        const total = this.segundosReserva();
        const minutos = Math.floor(total / 60).toString().padStart(2, '0');
        const segundos = (total % 60).toString().padStart(2, '0');
        return `${minutos}:${segundos}`;
    }

    private iniciarTimer() {
        this.detenerTimer();
        this.tickTimer = setInterval(() => {
            const restante = this.segundosReserva() - 1;
            if (restante <= 0) {
                this.detenerTimer();
                this.segundosReserva.set(0);
                this.reserva.set(null);
                this.updateField('establecimientoId', null);
                this.reservaError.set('Tu reserva expiró. Vuelve a elegir la entrega.');
                this.currentStep.set(2);
                return;
            }
            this.segundosReserva.set(restante);
        }, 1000);
    }

    private detenerTimer() {
        if (this.tickTimer) {
            clearInterval(this.tickTimer);
            this.tickTimer = null;
        }
    }

    ngOnDestroy(): void {
        this.detenerTimer();
        const cartUuid = this.cartService.cartUuid();
        if (!this.pedidoCerrado && cartUuid && this.reserva() !== null) {
            this.carritoBack.liberarReserva(cartUuid).subscribe({ next: () => undefined, error: () => undefined });
        }
    }

    private buildCheckoutPayload(estado: string): CarritoCheckoutPayload {
        const f = this.form();
        const isBoleta = f.voucherType === 'boleta';
        const isDelivery = f.deliveryType === 'delivery';
        const isSelf = f.recipientType === 'self';

        return {
            estado,
            tipoComprobanteId: isBoleta ? 1 : 2,
            tipoDocumentoId: f.docNumber.trim().length === 11 ? 2 : 1,
            numeroDocumento: f.docNumber.trim(),
            nombre: isBoleta ? f.firstName.trim() : null,
            apellidos: isBoleta ? f.lastName.trim() : null,
            razonSocial: isBoleta ? null : `${f.firstName} ${f.lastName}`.trim(),
            correo: f.email.trim(),
            telefono: this.digits(f.phone),
            tipoEntregaId: isDelivery ? 1 : 2,
            tipoDestinatarioId: isSelf ? 1 : 2,
            destinatarioNombre: isSelf ? f.firstName.trim() : f.recipientName.trim(),
            destinatarioApellidos: isSelf ? f.lastName.trim() : f.recipientLastName.trim(),
            destinatarioCelular: this.digits(isSelf ? f.phone : f.recipientPhone),
            distrito: null,
            direccion: isDelivery ? f.address.trim() : null,
            referencia: isDelivery ? (f.reference?.trim() || null) : null,
            latitud: f.location?.lat ?? null,
            longitud: f.location?.lng ?? null,
            empresaMetodoPagoUuid: this.metodoSeleccionado()?.uuid ?? null
        };
    }

    submitFinalOrder() {
        if (!this.step1Valid() || !this.step2Valid() || !this.step3Valid() || this.submitting()) {
            this.showErrors.set(true);
            const f = this.form();
            const target =
                !f.paymentMethod ? 's3-yape' :
                !f.voucherFile ? 's3-voucher' : 's3-terms';
            const el = document.getElementById(target) as HTMLElement | null;
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.focus();
            }
            return;
        }
        if (!this.reservaActiva()) {
            this.submitError.set('Tu reserva expiró. Vuelve al paso de entrega.');
            this.currentStep.set(2);
            return;
        }
        const cartUuid = this.cartService.cartUuid();
        const voucher = this.form().voucherFile;
        if (!cartUuid || !voucher) {
            this.submitError.set('Tu carrito expiró. Vuelve a agregar tus productos.');
            return;
        }
        const metodoUuid = this.resolveMetodoPagoUuid();
        if (!metodoUuid) {
            this.submitError.set('No hay un medio de pago disponible para tu elección.');
            return;
        }

        this.submitError.set(null);
        this.submitting.set(true);

        const email = this.form().email.trim();
        const telefono = this.digits(this.form().phone);
        const total = this.orderTotal();
        this.pedidos.confirmarPedido(this.buildPayload(cartUuid, metodoUuid)).subscribe({
            next: (tx) => {
                this.pagos.pagarConVoucher(tx.pedidoUuid, voucher).subscribe({
                    next: () => {
                        this.submitting.set(false);
                        this.mostrarExito(tx.pedidoUuid, tx.numeroPedido, email, telefono, total);
                        this.cartService.clearCart();
                    },
                    error: (err) => {
                        this.submitting.set(false);
                        this.mostrarExito(tx.pedidoUuid, tx.numeroPedido, email, telefono, total);
                        this.submitError.set(
                            'Tu pedido se registró, pero no pudimos subir el comprobante: ' +
                            (err?.message || 'inténtalo desde Mis pedidos.')
                        );
                        this.cartService.clearCart();
                    }
                });
            },
            error: (err) => {
                this.submitting.set(false);
                this.submitError.set(err?.message || 'No se pudo confirmar tu pedido.');
            }
        });
    }

    mostrarExito(pedidoUuid: string, numero: string, email: string, telefono: string, total: number) {
        this.pedidoCerrado = true;
        this.detenerTimer();
        this.pedidoUuid.set(pedidoUuid);
        this.pedidoNumero.set(numero);
        this.pedidoEmail.set(email);
        this.pedidoTelefono.set(telefono);
        this.pedidoTotal.set(total);
        this.copiado.set(false);
        this.infoCopiada.set(false);
    }

    infoPedido(): string {
        return `Rosaline - Pedido #${this.pedidoNumero()} por S/ ${this.pedidoTotal().toFixed(2)}. Rastrea tu compra con este número y tu celular aquí: ${window.location.origin}/seguimiento`;
    }

    copiarInfo() {
        const done = () => this.infoCopiada.set(true);
        try {
            const clip = (navigator as Navigator & { clipboard?: { writeText(t: string): Promise<void> } }).clipboard;
            if (clip) {
                clip.writeText(this.infoPedido()).then(done).catch(done);
            } else {
                done();
            }
        } catch {
            done();
        }
    }

    waPropioLink(): string {
        const to = WhatsappUtility.toWaNumber(this.pedidoTelefono());
        if (!to) {
            return '';
        }
        return WhatsappUtility.waLink(this.pedidoTelefono(), this.infoPedido());
    }

    copiarCodigo() {
        const numero = this.pedidoNumero();
        if (!numero) {
            return;
        }
        try {
            const done = () => this.copiado.set(true);
            const clip = (navigator as Navigator & { clipboard?: { writeText(t: string): Promise<void> } }).clipboard;
            if (clip) {
                clip.writeText(numero).then(done).catch(done);
            } else {
                done();
            }
        } catch {
            this.copiado.set(true);
        }
    }

    irRastrear() {
        this.router.navigate(['/seguimiento']);
    }

    irTienda() {
        this.router.navigate(['/']);
    }

    private buildPayload(cartUuid: string, metodoUuid: string): ConfirmarPedidoPayload {
        const f = this.form();
        const isBoleta = f.voucherType === 'boleta';
        const isDelivery = f.deliveryType === 'delivery';
        const isSelf = f.recipientType === 'self';

        return {
            carritoUuid: cartUuid,
            tipoComprobanteId: isBoleta ? 1 : 2,
            tipoDocumentoId: f.docNumber.trim().length === 11 ? 2 : 1,
            numeroDocumento: f.docNumber.trim(),
            nombre: isBoleta ? f.firstName.trim() : null,
            apellidos: isBoleta ? f.lastName.trim() : null,
            razonSocial: isBoleta ? null : `${f.firstName} ${f.lastName}`.trim(),
            correo: f.email.trim(),
            telefono: f.phone.trim(),
            tipoEntregaId: isDelivery ? 1 : 2,
            tipoDestinatarioId: isSelf ? 1 : 2,
            destinatarioNombre: isSelf ? f.firstName.trim() : f.recipientName.trim(),
            destinatarioApellidos: isSelf ? f.lastName.trim() : f.recipientLastName.trim(),
            destinatarioCelular: isSelf ? f.phone.trim() : f.recipientPhone.trim(),
            mensajeTarjeta: f.cardMessage?.trim() || null,
            direccion: isDelivery ? f.address.trim() : null,
            distrito: null,
            referencia: isDelivery ? (f.reference?.trim() || null) : null,
            latitud: f.location?.lat ?? null,
            longitud: f.location?.lng ?? null,
            empresaMetodoPagoUuid: metodoUuid,
            establecimientoId: f.establecimientoId ?? null,
            aceptoTerminos: true
        };
    }

    private resolveMetodoPagoUuid(): string | null {
        return this.metodoSeleccionado()?.uuid ?? null;
    }
}
